/**
 * Tokens visuais do bloco de oferta do Botão de Ação.
 *
 * Fonte única de verdade do editor e do preview React. O player embed
 * (`e-player-js/hostvsl-player.js`) replica estes mesmos números em vanilla JS —
 * ao mudar qualquer valor aqui, espelhar lá.
 */

import type {
  ActionButtonTextSize,
  ActionButtonTextWeight,
} from '@/store/videoFeatures/types/action-button-templates';

/** Elementos textuais que têm escala própria de tamanho. */
export type ActionButtonTextElementKey =
  | 'urgency'
  | 'headline'
  | 'subheadline'
  | 'offerText'
  | 'strikethrough'
  | 'featured'
  | 'price'
  | 'priceNote'
  | 'secondary';

type SizeScale = Record<ActionButtonTextSize, number>;

/**
 * Tamanho em px por elemento. Cada elemento tem a própria escala porque um
 * "grande" de preço (38px) e um "grande" de nota de preço (17px) não são a
 * mesma coisa.
 */
export const TEXT_SIZE_PX: Record<ActionButtonTextElementKey, SizeScale> = {
  urgency: { small: 12, medium: 14, large: 16, 'extra-large': 18 },
  headline: { small: 20, medium: 24, large: 30, 'extra-large': 36 },
  subheadline: { small: 12, medium: 14, large: 17, 'extra-large': 20 },
  offerText: { small: 12, medium: 14, large: 17, 'extra-large': 20 },
  strikethrough: { small: 14, medium: 16, large: 20, 'extra-large': 24 },
  featured: { small: 22, medium: 26, large: 32, 'extra-large': 40 },
  price: { small: 24, medium: 30, large: 38, 'extra-large': 46 },
  priceNote: { small: 12, medium: 14, large: 17, 'extra-large': 20 },
  secondary: { small: 12, medium: 14, large: 16, 'extra-large': 18 },
};

/** Peso semântico → `font-weight` numérico. */
export const TEXT_WEIGHT: Record<ActionButtonTextWeight, number> = {
  normal: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
  'extra-bold': 900,
};

/**
 * Geometria do botão primário/secundário, chaveada pelo `size` do botão.
 *
 * `minWidthSplit` vale quando dois botões dividem a mesma linha (`yes-no`):
 * a largura mínima cai para caberem lado a lado sem quebrar.
 */
export const BUTTON_GEOMETRY = {
  small: { height: 40, padding: '8px 16px', fontSize: 13, minWidth: 160, minWidthSplit: 120 },
  medium: { height: 48, padding: '12px 24px', fontSize: 15, minWidth: 180, minWidthSplit: 140 },
  large: { height: 56, padding: '16px 32px', fontSize: 17, minWidth: 220, minWidthSplit: 180 },
} as const;

export type ActionButtonSize = keyof typeof BUTTON_GEOMETRY;

/** Sombra do botão. Mesmos valores do player. */
export const BUTTON_SHADOW = {
  normal: '0 3px 10px rgba(0, 0, 0, 0.4)',
  hover: '0 5px 15px rgba(0, 0, 0, 0.5)',
} as const;

/** Largura máxima do bloco de oferta. */
export const TEMPLATE_MAX_WIDTH = 820;

/** Resolve o tamanho em px de um elemento, caindo no default do elemento. */
export function textSizePx(
  element: ActionButtonTextElementKey,
  size: ActionButtonTextSize | undefined,
  fallback: ActionButtonTextSize = 'medium',
): number {
  const scale = TEXT_SIZE_PX[element];
  return scale[size ?? fallback] ?? scale[fallback];
}

/** Resolve o peso numérico, caindo no default informado. */
export function textWeight(
  weight: ActionButtonTextWeight | undefined,
  fallback: ActionButtonTextWeight = 'normal',
): number {
  return TEXT_WEIGHT[weight ?? fallback] ?? TEXT_WEIGHT[fallback];
}

/** Geometria do botão, tolerante a `size` ausente ou fora do domínio. */
export function buttonGeometry(size: string | undefined) {
  return BUTTON_GEOMETRY[(size as ActionButtonSize) ?? 'medium'] ?? BUTTON_GEOMETRY.medium;
}
